import type { FC, ReactNode } from 'react'

import { useState } from 'react'
import {
    Pause,
    Play,
    SkipBack,
    SkipForward
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { getDisplayRatio } from '@/lib/photos'
import { useReducedMotion } from '@/hooks/use-reduced-motion'
import MemoryPhoto from '@/components/landing/memory-photo'
import { GALLERY, VIDEOS } from '@/constants/birthday'

const SLIDE_MS = 3800

type FilmStatus = 'idle' | 'playing' | 'paused' | 'ended'

// The 7 photos played one after another like a short film.
const PhotoFilm: FC = (): ReactNode => {
    const [status, setStatus] = useState<FilmStatus>('idle')
    const [index, setIndex] = useState(0)
    const [run, setRun] = useState(0)
    const reducedMotion = useReducedMotion()
    const items = GALLERY.items
    const total = items.length
    const running = status === 'playing'
    const showOverlay = status === 'idle' || status === 'ended'

    const start = (): void => {
        setIndex(0)
        setRun((value) => value + 1)
        setStatus('playing')
    }

    const advance = (): void => {
        if (index >= total - 1) {
            setStatus('ended')
            return
        }

        setIndex(index + 1)
    }

    const jump = (direction: number): void => {
        setIndex((value) => Math.min(Math.max(value + direction, 0), total - 1))
        setRun((value) => value + 1)
    }

    return (
        <div className='mx-auto max-w-4xl'>
            <div className='relative aspect-[4/5] overflow-hidden rounded-[2rem] bg-black shadow-2xl shadow-black/40 ring-1 ring-white/10 [container-type:size] sm:aspect-video'>
                {items.map((item, slide) => {
                    const visible = slide === index
                    const ratio = getDisplayRatio(item.photo)

                    return (
                        <div
                            key={item.photo.src}
                            aria-hidden={!visible}
                            className={cn(
                                'absolute inset-0 transition-opacity duration-1000 motion-reduce:transition-none',
                                visible ? 'opacity-100' : 'opacity-0'
                            )}
                        >
                            <MemoryPhoto
                                photo={item.photo}
                                className='absolute inset-0 h-full scale-110 opacity-45 blur-2xl'
                            />

                            <div
                                className={cn(
                                    'absolute inset-0 grid place-items-center',
                                    visible && status !== 'idle' && !reducedMotion && 'animate-ken-burns',
                                    status === 'paused' && '[animation-play-state:paused]'
                                )}
                            >
                                <MemoryPhoto
                                    photo={item.photo}
                                    loading={slide === 0 ? 'eager' : 'lazy'}
                                    className='rounded-2xl shadow-2xl shadow-black/50'
                                    style={{ width: `min(90cqw, ${(ratio * 86).toFixed(2)}cqh)` }}
                                />
                            </div>

                            {status !== 'idle' && (
                                <p className='absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent px-6 pb-6 pt-20 text-center font-hand text-2xl leading-snug text-white sm:text-3xl'>
                                    {item.caption}
                                </p>
                            )}
                        </div>
                    )
                })}

                {status !== 'idle' && (
                    <div aria-hidden className='absolute inset-x-4 top-4 z-20 flex gap-1.5'>
                        {items.map((item, slide) => {
                            const active = slide === index && status !== 'ended'

                            return (
                                <span key={item.photo.src} className='h-1 flex-1 overflow-hidden rounded-full bg-white/25'>
                                    <span
                                        key={active ? `active-${run}-${index}` : `idle-${slide}`}
                                        onAnimationEnd={active ? advance : undefined}
                                        className={cn(
                                            'block h-full origin-left rounded-full bg-white',
                                            active ? 'animate-slide-fill' : slide < index || status === 'ended' ? 'scale-x-100' : 'scale-x-0'
                                        )}
                                        style={active ? { animationDuration: `${SLIDE_MS}ms`, animationPlayState: running ? 'running' : 'paused' } : undefined}
                                    />
                                </span>
                            )
                        })}
                    </div>
                )}

                {showOverlay && (
                    <div className='absolute inset-0 z-30 grid place-items-center bg-black/45 p-6 text-center'>
                        <button
                            type='button'
                            onClick={start}
                            className='group flex flex-col items-center rounded-3xl p-4 text-white focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-white/60'
                        >
                            <span className='grid size-20 place-items-center rounded-full bg-primary text-primary-foreground shadow-2xl shadow-primary/40 transition group-hover:scale-105 sm:size-24'>
                                <Play className='ml-1 size-8 fill-current sm:size-10' />
                            </span>

                            <span className='mt-5 font-heading text-2xl font-semibold sm:text-3xl'>
                                {VIDEOS.film.play}
                            </span>

                            <span className='mt-2 text-sm font-semibold text-white/75'>
                                {VIDEOS.film.meta}
                            </span>
                        </button>
                    </div>
                )}
            </div>

            {!showOverlay && (
                <div className='mt-5 flex items-center justify-center gap-3'>
                    <button
                        type='button'
                        onClick={() => jump(-1)}
                        disabled={index === 0}
                        aria-label={VIDEOS.film.previous}
                        className='grid size-11 place-items-center rounded-full bg-white/10 text-white transition hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white disabled:opacity-40'
                    >
                        <SkipBack className='size-5' />
                    </button>

                    <button
                        type='button'
                        onClick={() => setStatus(running ? 'paused' : 'playing')}
                        aria-label={running ? VIDEOS.film.pause : VIDEOS.film.resume}
                        className='grid size-14 place-items-center rounded-full bg-primary text-primary-foreground shadow-lg shadow-black/30 transition hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white'
                    >
                        {running ? <Pause className='size-6 fill-current' /> : <Play className='ml-0.5 size-6 fill-current' />}
                    </button>

                    <button
                        type='button'
                        onClick={() => jump(1)}
                        disabled={index === total - 1}
                        aria-label={VIDEOS.film.next}
                        className='grid size-11 place-items-center rounded-full bg-white/10 text-white transition hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white disabled:opacity-40'
                    >
                        <SkipForward className='size-5' />
                    </button>

                    <p className='ml-2 min-w-12 text-sm font-bold tabular-nums text-white/70'>
                        {index + 1} / {total}
                    </p>
                </div>
            )}
        </div>
    )
}

export default PhotoFilm
